import React from 'react';
import PropTypes from 'prop-types';
import Icon from '@hobs/components/Icon';
import Confirm from '@hobs/components/Confirm';
import Panel from '@hobs/components/Panel';
import Input from '@hobs/components/Input';
import Textarea from '@hobs/components/Textarea';
import PageActions from '@hobs/components/PageActions';
import Autocomplete from '@hobs/components/Autocomplete';
import objectPath from 'object-path';
import Dropdown from '@hobs/components/Dropdown';
import DropZone from '@hobs/components/DropZone';
import Status from '@hobs/components/Status';
import { format } from '@hobs/utils/formatWithTimeZone';
import {
  USER_ROLES,
  SERVICE_REQUEST_NAVIGATION_ORIGIN,
  SERVICE_REQUEST_FOOTER_STATUSES,
  DATE_FORMAT
} from '@hobs/utils/constants';
import { GhostButton, RaisedButton } from '@hobs/components/Buttons';
import { H1, H2, H5 } from '@hobs/components/Headings';
import Toast from '@hobs/components/Toast';
import InputLabel from '@hobs/components/InputLabel';
import FieldHistoryMessage from '@hobs/components/FieldHistoryMessage';
import ShortId from 'shortid';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import { ServiceRequestNavContext } from '@hobs/components/ServiceRequestNavContext';
import DeliveryInformation from '@hobs/components/DeliveryInformation';
import LocationDetails from '@hobs/components/LocationDetails';
import CatalogItemInfo from '@hobs/components/CatalogItemInfo';
import AssignmentResponse from '@hobs/components/AssignmentResponse';
import PageLinks from '@hobs/components/PageLinks';
import ServiceRequestFooter from '@hobs/components/ServiceRequestFooter';
import ChangeRequestCreateDialog from '@hobs/containers/ChangeRequestCreateDialog';
import ActionWithCommentDialog from '@hobs/containers/ActionWithCommentDialog';
import './ServiceRequestCustom.scss';
import InformationTooltip from '@hobs/components/InformationToolTip';
import CancellationCosts from '@hobs/components/CancellationCosts';
import GeneralAttachments from '../GeneralAttachments/GeneralAttachments';
import AssignmentDelivery from '@hobs/components/AssignmentDelivery';

class ServiceRequestCustom extends React.PureComponent {
  constructor(props) {
    super(props);
    const { t } = props;
    this.container = React.createRef();

    this.state = {
      itemChanged: false,
      attachments: [],
      calculatedCostValues: {},
      showChangeItem: false,
      isEarlyConfirmationSubmitting: false,
      serviceRequest: {
        isTransportation: false
      },
      oDataSearchFilter: {},
      dataState: { take: 10, skip: 0, sort: [{ field: 'Code', dir: 'asc' }] },
      disabled: true,
      location: {},
      responseFileDialogOpen: false,
      changeRequestResponseFileDialogOpen: false,
      ...this.props.location.state,
      navigation: {}
    };

    this.validationSchema = Yup.object().shape({
      annexId: Yup.string().when('hasDefaultAnnex', {
        is: false,
        then: Yup.string()
          .required(t('Annex is required'))
          .nullable(),
        otherwise: Yup.string().nullable()
      }),
      serviceName: Yup.string()
        .required()
        .max(128, t('Service name cannot be longer than 128 symbols'))
        .required(t('Service name is required')),
      serviceDescription: Yup.string()
        .max(500, t('Service description cannot be longer than 500 symbols'))
        .required(t('Service description is required')),
      unitId: Yup.string().required(t('Unit is required')),
      unitOfMeasureId: Yup.string().required(t('UoM is required')),
      dateFrom: Yup.date()
        .required(t('From date is required'))
        .typeError(t('From date is required')),
      quantity: Yup.number()
        .typeError('Invalid number')
        .positive(t('Quantity must be greater than 0'))
        .required(t('Quantity is required')),
      comment: Yup.string()
        .nullable()
        .max(1500, t('Comment cannot be longer than 1500 symbols')),
      location: Yup.object().when('isFsorPhaseOrLater', {
        is: true,
        then: Yup.object()
          .required(t('Location is required'))
          .nullable(),
        otherwise: Yup.object().nullable()
      })
    });
  }

  calculateCosts(values) {
    const { calculateByDays } = this.props;

    if (!values.dateFrom || !values.quantity) {
      return;
    }

    const currentValues = {
      dateFrom: values.dateFrom,
      dateTo: values.dateTo,
      quantity: values.quantity,
      isAccommodation: values.isAccommodation,
      isCalculatedByDays: values.isCalculatedByDays,
      rateFrom: values.rateFrom,
      rateTo: values.rateTo
    };

    var currentState = this.state.calculatedCostValues;

    if (JSON.stringify(currentState) !== JSON.stringify(currentValues)) {
      calculateByDays(currentValues);
      this.setState({ calculatedCostValues: currentValues });
    }
  }

  cancelForm(dirty, navContext) {
    const { history, t } = this.props;
    if (!dirty) {
      history.push(this.getBackNavigationUrl(navContext));
    } else {
      Confirm(
        t('Cancel service request'),
        t('Are you sure you want to cancel? All entered data will be lost.'),
        () => {
          history.push(this.getBackNavigationUrl(navContext));
        }
      );
    }
  }

  componentDidMount() {
    this.loadRequiredData();
  }

  componentDidUpdate(prevProps) {
    const { match } = this.props;
    if (
      match.params.serviceRequestId !== prevProps.match.params.serviceRequestId
    ) {
      this.loadRequiredData();
    }
  }

  navigateBack = navContext => () => {
    const { history } = this.props;
    history.push(this.getBackNavigationUrl(navContext));
  };

  loadRequiredData = () => {
    const {
      forSubmit,
      match,
      getServiceRequest,
      getFooter,
      setAttachments
    } = this.props;

    forSubmit(match.params.partyId);

    if (this.isEdit()) {
      getServiceRequest(match.params.serviceRequestId).then(({ payload }) => {
        this.setState({
          serviceRequest: payload,
          location: {
            original: {
              name: payload.locationName,
              latitude: payload.latitude,
              longtitude: payload.longitude,
              site: payload.site,
              region: {
                name: payload.regionName,
                regionId: payload.regionId
              }
            }
          },
          disabled:
            !payload.canBeEdited ||
            this.isOnlyPreviewMode() ||
            this.isEarlyConfirmationRequiredAndNotEditable(payload)
        });
        setAttachments(payload.attachments);
        getFooter(match.params.serviceRequestId);
      });
      this.queryOdata();
    } else {
      this.setState({
        disabled: false
      });
    }
  };

  componentWillUnmount() {
    const {
      resetOData,
      clearAttachments,
      clearCalculateByDays,
      clearFooter
    } = this.props;

    resetOData();
    clearAttachments();
    clearCalculateByDays();
    clearFooter();
  }

  isEarlyConfirmationRequiredAndNotEditable(payload) {
    return (
      payload.response &&
      payload.response.earlyConfirmation.isEarlyConfirmationRequired &&
      !payload.response.earlyConfirmation.isEditable
    );
  }

  getServiceRequestsGridUrl() {
    return `/service-requests/${this.props.match.params.partyId}`;
  }

  getCatalogItemsQueryUrl() {
    return `CatalogItemsQueryForServiceRequest(${this.props.match.params.partyId})`;
  }

  getHistoryPath() {
    const { match } = this.props;
    return `/custom-service-request-history/${match.params.partyId}/${match.params.serviceRequestId}`;
  }

  getEditPath() {
    const { match } = this.props;
    return `/custom-service-request/${match.params.partyId}/${match.params.serviceRequestId}`;
  }

  getShowPath() {
    const { match } = this.props;
    return `/request-information/${match.params.serviceRequestId}`;
  }

  getChangeHistoryPath() {
    const { match } = this.props;
    return `/service-request/${match.params.partyId}/${match.params.serviceRequestId}/change-history`;
  }

  getDiscussionInitials() {
    const { t } = this.props;
    return t('SN');
  }

  getDiscussionPath() {
    const { match } = this.props;
    return `${match.url}/discussion`;
  }

  isEdit() {
    return !!objectPath.get(this.props.match.params, 'serviceRequestId');
  }

  hasDefaultAnnex() {
    const { forSubmitData } = this.props;

    return (
      forSubmitData && forSubmitData.annexes.some(annex => annex.isDefault)
    );
  }

  isAdditionalRequest() {
    const { forSubmitData } = this.props;
    const { serviceRequest } = this.state;

    if (this.isEdit()) {
      return serviceRequest.isAdditionalRequest;
    }

    return forSubmitData.isAdditionalSrCreateForm;
  }

  isFsorPhaseOrLater() {
    const { forSubmitData } = this.props;
    return forSubmitData.isFsorPhaseOrLater;
  }

  getAnnexOptions = () => {
    const { t, forSubmitData } = this.props;
    const annexes = [...forSubmitData.annexes];
    const { serviceRequest } = this.state;

    if (
      this.isEdit() &&
      serviceRequest &&
      !annexes.some(annex => annex.annexId === serviceRequest.annexId)
    ) {
      annexes.push({
        annexName: serviceRequest.annexName,
        annexId: serviceRequest.annexId
      });
    }

    if (annexes.length) {
      return []
        .concat(
          [
            {
              annexName: t('Select annex'),
              annexId: ''
            }
          ],
          annexes
        )
        .map(annex => {
          return (
            <option key={ShortId.generate()} value={annex.annexId}>
              {annex.annexName}
            </option>
          );
        });
    }

    return (
      <option value="">{t('There are no Annexes for this Exercise')}</option>
    );
  };

  getUnits = units => {
    const { serviceRequest } = this.state;
    const newUnits = this.flattenUnitsTree(units).map(unit => {
      return {
        title: unit.unitName,
        unitId: unit.unitId
      };
    });

    if (
      this.isEdit() &&
      serviceRequest &&
      !newUnits.some(unit => unit.unitId === serviceRequest.unitId)
    ) {
      newUnits.push({
        title: serviceRequest.unitName,
        unitId: serviceRequest.unitId
      });
    }

    return this.toUnitsEntity(newUnits);
  };

  flattenUnitsTree(array, level = 0) {
    if (!Array.isArray(array)) {
      return [];
    }

    return array.reduce((result, item) => {
      result.push({
        unitName: `${'\xa0\xa0\xa0'.repeat(level)}${item.unitName}`,
        unitId: item.unitId
      });

      if (Array.isArray(item.units)) {
        result = result.concat(this.flattenUnitsTree(item.units, level + 1));
      }

      return result;
    }, []);
  }

  getGroupOptions = annexId => {
    const { t, forSubmitData } = this.props;
    const groups = [...forSubmitData.groups];
    const { serviceRequest } = this.state;

    if (
      this.isEdit() &&
      serviceRequest &&
      !groups.some(group => group.groupName === serviceRequest.groupName)
    ) {
      groups.push({
        groupName: serviceRequest.groupName,
        groupId: serviceRequest.groupId,
        annexId: serviceRequest.annexId
      });
    }

    if (groups.length) {
      const filteredGroups = annexId
        ? groups.filter(group => group.annexId === annexId)
        : groups;

      const placeholder = (
        <option key={ShortId.generate()} value="">
          {t('Select group')}
        </option>
      );

      const options = filteredGroups.map(group => {
        return (
          <option key={ShortId.generate()} value={group.groupName}>
            {group.groupName}
          </option>
        );
      });
      return [placeholder, options];
    }

    return (
      <option value="">{t('There are no Groups for this Exercise')}</option>
    );
  };

  setAnnexValue = (event, setFieldValue) => {
    const { groups } = this.props.forSubmitData;
    if (groups) {
      const currentGroup = groups.filter(
        group => group.groupName === event.target.value
      )[0];

      if (currentGroup && currentGroup.annexId) {
        setFieldValue('annexId', currentGroup.annexId);
      }
    }
  };

  toUnitsEntity(units) {
    return units
      ? units.map(unit => {
          return {
            title: unit.title,
            value: unit.unitId
          };
        })
      : [];
  }

  toUomsEntity(uoms) {
    return uoms
      ? uoms.map(uom => {
          return {
            title: uom.value || uom.UnitOfMeasureCode,
            value: uom.key || uom.UnitOfMeasureId
          };
        })
      : [];
  }

  toActivityEntity(activities) {
    return activities
      ? activities.map(activity => {
          return {
            name: activity.value || activity.name,
            activityId: activity.key || activity.activityId,
            isDefault: activity.isDefault
          };
        })
      : [];
  }

  onLocationQueryChange = query => {
    this.props.queryLocations(query);
  };

  handleFileUpload = value => {
    if (value) {
      this.props.uploadAttachments({ attachments: value });
    }
  };

  _customCreate(data) {
    const { create, createCustomAdditionalServiceRequest } = this.props;
    return this.isAdditionalRequest()
      ? createCustomAdditionalServiceRequest(data)
      : create(data);
  }

  getCustomServiceRequestEditUrl(serviceRequestId) {
    const { match } = this.props;

    return `/custom-service-request/${match.params.partyId}/${serviceRequestId}`;
  }

  onSubmit = (values, { setErrors, setSubmitting }) => {
    const { t, updateCustom, match, update, history } = this.props;
    if (
      values.dateTo &&
      new Date(format(Date.parse(values.dateFrom), DATE_FORMAT)) >
        new Date(format(Date.parse(values.dateTo), DATE_FORMAT))
    ) {
      Toast.warning(t('From date must be before To date'));
      setSubmitting(false);
      return;
    }

    const submitData = this.getSubmitData(values);

    if (this.isEdit()) {
      let updateFn = updateCustom;
      const data = {
        serviceRequestId: match.params.serviceRequestId,
        ...submitData
      };

      if (this.state.serviceRequest.catalogItemId) {
        data.catalogItemId = this.state.serviceRequest.catalogItemId;
        updateFn = update;
      }

      updateFn(data).then(response => {
        if (response.success) {
          Toast.success(t('Service request was successfully updated'));
          history.push(this.getBackNavigationUrl(this.state.navigation));
        } else {
          if (!response.payload.details) {
            Toast.warning(t(response.payload.message));
          } else {
            const errors = response.payload.details.reduce((results, error) => {
              return {
                ...results,
                [error.target]: error.message[0]
              };
            }, {});

            setErrors(errors);
          }
        }

        setSubmitting(false);
      });
    } else {
      this._customCreate(submitData).then(response => {
        if (response.success) {
          Toast.success(
            this.isAdditionalRequest()
              ? t('Additional service request submitted successfully')
              : t('Service request was successfully created')
          );

          if (this.isAdditionalRequest()) {
            history.push(
              this.getCustomServiceRequestEditUrl(
                response.payload.serviceRequestId
              )
            );
          } else {
            history.push(this.getBackNavigationUrl(this.state.navigation));
          }
        } else {
          if (!response.payload.details) {
            Toast.warning(t(response.payload.message));
          } else {
            const errors = response.payload.details.reduce((results, error) => {
              return {
                ...results,
                [error.target]: error.message[0]
              };
            }, {});

            setErrors(errors);
          }
        }

        setSubmitting(false);
      });
    }
  };

  getSubmitData(values) {
    const { forSubmitData, attachments } = this.props;

    return {
      groupName: values.groupName ? values.groupName : null,
      annexId: values.annexId,
      serviceName: values.serviceName,
      serviceDescription: values.serviceDescription,
      unitOfMeasureId: values.unitOfMeasureId,
      statementOfRequirementId: forSubmitData.statementOfRequirementId,
      unitId: values.unitId,
      requestedQuantity: values.quantity,
      dateFrom: values.dateFrom,
      dateTo: values.dateTo,
      isCalculatedByDays: values.isCalculatedByDays,
      regionId: objectPath.get(
        values.location,
        'original.region.regionId',
        null
      ),
      regionName: objectPath.get(values.location, 'original.region.name', null),
      locationName: objectPath.get(values.location, 'original.name', null),
      locationComment: values.locationComment,
      site: objectPath.get(values.location, 'original.site', null),
      latitude: objectPath.get(values.location, 'original.latitude', null),
      longitude: objectPath.get(values.location, 'original.longtitude', null),
      regionIdFrom: objectPath.get(
        values.locationFrom,
        'original.region.regionId',
        null
      ),
      regionNameFrom: objectPath.get(
        values.locationFrom,
        'original.region.name',
        null
      ),
      locationNameFrom: objectPath.get(
        values.locationFrom,
        'original.name',
        null
      ),
      locationCommentFrom: values.locationCommentFrom,
      siteFrom: objectPath.get(values.locationFrom, 'original.site', null),
      latitudeFrom: objectPath.get(
        values.locationFrom,
        'original.latitude',
        null
      ),
      longitudeFrom: objectPath.get(
        values.locationFrom,
        'original.longtitude',
        null
      ),
      comment: values.comment,
      activityId: values.activityId,
      attachments: attachments
        ? attachments.map(attachment => attachment.attachmentId)
        : []
    };
  }

  _remove() {
    const { match, remove } = this.props;

    return remove(match.params.partyId, match.params.serviceRequestId);
  }

  onRemove = () => {
    const { t } = this.props;
    Confirm(
      t('Delete service request'),
      t(
        'Are you sure you want to delete this Service request? It will be removed from your SOR.'
      ),
      () => {
        const { history } = this.props;

        this._remove().then(response => {
          if (response.success) {
            Toast.success(t('Service request was successfully deleted'));
            history.push(this.getBackNavigationUrl(this.state.navigation));
          } else {
            Toast.warning(response.payload.message);
          }
        });
      }
    );
  };

  onResponseFileDialogOpen = () => {
    this.setState({
      responseFileDialogOpen: true
    });
  };

  onResponseFileDialogClose = () => {
    this.setState({
      responseFileDialogOpen: false
    });
  };

  onDeliveryFileDialogOpen = () => {
    this.setState({
      deliveryFileDialogOpen: true
    });
  };

  onDeliveryFileDialogClose = () => {
    this.setState({
      deliveryFileDialogOpen: false
    });
  };

  onChangeRequestResponseFileDialogOpen = () => {
    this.setState({
      changeRequestResponseFileDialogOpen: true
    });
  };

  onChangeRequestResponseFileDialogClose = () => {
    this.setState({
      changeRequestResponseFileDialogOpen: false
    });
  };

  formInitialValues() {
    const { serviceRequest, forSubmitData } = this.props;

    if (this.isEdit()) {
      const group = forSubmitData.groups.find(
        group => group.groupName === serviceRequest.groupName
      );

      const data = {
        ...serviceRequest,
        quantity: serviceRequest.requestedQuantity,
        serviceDescription: serviceRequest.description,
        serviceName: serviceRequest.name,
        location: this.state.location,
        activityId: this.getInitialActivity()
      };

      if (group) {
        data.groupId = group.groupId; // HACK!
      }

      return data;
    }

    return {
      // information
      annexId: '',
      groupId: '',
      serviceName: '',
      serviceDescription: '',
      quantity: '', // requestedQuantity
      dateFrom: '',
      dateTo: '',
      isCalculatedByDays: false,
      unitOfMeasureId: '',
      statementOfRequirementId: '',
      unitId: '',
      // locations
      location: null,
      regionId: '',
      regionName: '',
      locationName: '',
      locationComment: '',
      isTransportation: false,
      isAccommodation: false,
      isMeal: false,
      isAdditionalRequest: this.isAdditionalRequest(),
      hasDefaultAnnex: this.hasDefaultAnnex(),
      site: '',
      latitude: '',
      longitude: '',
      // additional details
      activityId: this.getInitialActivity(),
      comment: '',
      attachments: [],
      isFsorPhaseOrLater: this.isFsorPhaseOrLater()
    };
  }

  getInitialActivity = () => {
    const activities = this.props.forSubmitData.activities;
    const serviceRequest = this.state.serviceRequest;

    if (!activities || activities.length <= 0) {
      return null;
    }

    if (serviceRequest.serviceRequestId === undefined) {
      const activity = this.toActivityEntity(activities).find(x => x.isDefault);
      if (activity) {
        return activity.activityId;
      }
    }

    return serviceRequest.activityId;
  };

  toggleChangeItem = () => {
    this.setState(prevState => ({
      showChangeItem: !prevState.showChangeItem
    }));
  };

  changeItem = ({ original }, setFieldValue) => {
    if (original) {
      const isAccommodationOrMeal = original.IsMeal || original.IsAccommodation;

      this.setState(
        {
          itemChanged: true,
          showChangeItem: false,
          serviceRequest: {
            ...this.state.serviceRequest,
            annexId: original.AnnexId,
            annexName: original.AnnexName,
            attachments: original.CatalogAttachments,
            catalogItemId: original.CatalogItemId,
            code: original.Code,
            description: original.Description,
            groupName: original.GroupName,
            name: original.Name,
            reateFrom: original.RateFrom,
            reateTo: original.RateTo,
            unitOfMeasureCode: objectPath.get(
              original.UnitsOfMeasure.find(item => item.IsDefault),
              'UnitOfMeasureCode',
              '-'
            ),
            unitsOfMeasure: original.UnitsOfMeasure,
            isTransportation: original.IsTransportation,
            isCalculatedByDays: isAccommodationOrMeal,
            catalogItemAttachments: original.CatalogAttachments.map(item => ({
              attachmentId: item.AttachmentId,
              name: item.Name
            }))
          }
        },
        () => {
          const uomDefault = original.UnitsOfMeasure.find(
            item => item.IsDefault
          );
          setFieldValue('rateFrom', original.RateFrom);
          setFieldValue('rateTo', original.RateTo);
          setFieldValue('unitOfMeasureId', uomDefault.UnitOfMeasureId);
          setFieldValue('annexId', original.AnnexId);
          setFieldValue('locationFrom', this.state.location);
          setFieldValue('itemChanged', 'true');
          setFieldValue('isAccommodation', original.IsAccommodation);
          setFieldValue('isCalculatedByDays', isAccommodationOrMeal);
        }
      );
    }
  };

  queryOdata() {
    const { oDataSearchFilter, dataState } = this.state;
    const filters = [];

    const params = {
      ...dataState,
      sort: [{ field: 'QuickSearchField', dir: 'asc' }]
    };

    if (Object.keys(oDataSearchFilter).length > 0) {
      filters.push(oDataSearchFilter);
    }

    if (filters.length > 0) {
      params.filter = {
        logic: 'and',
        filters
      };
    }

    this.props.queryOData(this.getCatalogItemsQueryUrl(), params);
  }

  toAutocompleteEntity(
    data,
    valueIndex = 'code',
    titleIndex = 'name',
    addServiceCode = false
  ) {
    return data
      ? data.map(item => ({
          value: item[valueIndex],
          title: addServiceCode
            ? `${item[valueIndex]} ${item[titleIndex]}`
            : item[titleIndex],
          original: item
        }))
      : [];
  }

  onItemQueryChange = query => {
    let state = {};

    if (query) {
      state = {
        oDataSearchFilter: {
          logic: 'and',
          filters: [
            {
              field: 'QuickSearchField',
              operator: 'contains',
              value: query
            }
          ]
        }
      };
    } else {
      state = {
        oDataSearchFilter: {}
      };
    }

    this.setState(state, () => {
      this.queryOdata();
    });
  };

  getBackNavigationUrl = navContext => {
    if (navContext === SERVICE_REQUEST_NAVIGATION_ORIGIN.AnnexOverview) {
      const { navigationHistory } = this.state;

      const withSearch =
        navigationHistory &&
        navigationHistory.location &&
        navigationHistory.location.search
          ? navigationHistory.location.search
          : '';
      return `/annex-overview${withSearch}`;
    } else if (navContext === SERVICE_REQUEST_NAVIGATION_ORIGIN.Orders) {
      return '/orders';
    } else if (navContext === SERVICE_REQUEST_NAVIGATION_ORIGIN.Tickets) {
      return '/tickets';
    } else if (
      navContext === SERVICE_REQUEST_NAVIGATION_ORIGIN.RequestedServices
    ) {
      return this.getServiceRequestsGridUrl();
    } else {
      return this.getServiceRequestsGridUrl();
    }
  };

  getServiceRequestCreateHeaderTitle() {
    const { t } = this.props;
    if (this.isAdditionalRequest()) {
      return t('Custom additional service request');
    } else {
      return t('Custom service request');
    }
  }

  getServiceRequestEditHeaderTitle() {
    const { t } = this.props;
    if (this.isAdditionalRequest()) {
      return t('Edit custom additional service request');
    } else {
      return t('Edit custom service request');
    }
  }

  getAdditionalCreateWaitingForHnFooter(dirty, isSubmitting, canEditItem) {
    const { t } = this.props;
    return (
      <ServiceRequestFooter
        message={t(
          'Service request is not confirmed yet. Waiting for response from HN.'
        )}
      >
        {canEditItem && (
          <div>
            <ServiceRequestNavContext.Consumer>
              {nav => (
                <GhostButton
                  onClick={() =>
                    this.cancelForm(dirty, nav.serviceRequestNavigation)
                  }
                  large
                >
                  {t('Cancel')}
                </GhostButton>
              )}
            </ServiceRequestNavContext.Consumer>
            <ServiceRequestNavContext.Consumer>
              {nav => (
                <RaisedButton
                  type="submit"
                  disabled={(!dirty && !this.state.itemChanged) || isSubmitting}
                  onClick={() => {
                    this.setState({
                      navigation: nav.serviceRequestNavigation,
                      navigationHistory: nav.previousPageHistory
                    });
                  }}
                  large
                >
                  {t('Save')}
                </RaisedButton>
              )}
            </ServiceRequestNavContext.Consumer>
          </div>
        )}
      </ServiceRequestFooter>
    );
  }

  getAdditionalEditableCreateWaitingForHnFooter(dirty, isSubmitting) {
    const { t } = this.props;
    return (
      <ServiceRequestFooter
        message={t(
          'Service request is not confirmed yet. Waiting for response from HN.'
        )}
      >
        <div>
          <ServiceRequestNavContext.Consumer>
            {nav => (
              <GhostButton
                onClick={() =>
                  this.cancelForm(dirty, nav.serviceRequestNavigation)
                }
                large
              >
                {t('Cancel')}
              </GhostButton>
            )}
          </ServiceRequestNavContext.Consumer>
          <ServiceRequestNavContext.Consumer>
            {nav => (
              <RaisedButton
                type="submit"
                disabled={(!dirty && !this.state.itemChanged) || isSubmitting}
                onClick={() => {
                  this.setState({
                    navigation: nav.serviceRequestNavigation,
                    navigationHistory: nav.previousPageHistory
                  });
                }}
                large
              >
                {t('Save')}
              </RaisedButton>
            )}
          </ServiceRequestNavContext.Consumer>
        </div>
      </ServiceRequestFooter>
    );
  }

  getAdditionalCreateWaitingForSnFooter() {
    const { t } = this.props;
    return (
      <ServiceRequestFooter
        message={t(
          'Response to this service request is not accepted yet. Waiting for confirmation by SN responsible.'
        )}
      />
    );
  }

  getAdditionalChangeWaitingForSnFooter() {
    const { t } = this.props;
    return (
      <ServiceRequestFooter
        message={t(
          'Response to this service request is not accepted yet. Waiting for confirmation by SN responsible.'
        )}
      />
    );
  }

  rejectChangeRequestResponse = () => {
    const { t, match, changeRequestRejectResponse } = this.props;
    Confirm(
      t('Reject response'),
      t("Are you sure, you want to reject HN's response?"),
      () => {
        changeRequestRejectResponse(match.params.serviceRequestId).then(
          response => {
            if (response.success) {
              Toast.success(t('Change request successfully rejected'));
              this.loadRequiredData();
            } else if (response.payload) {
              Toast.warning(response.payload.message);
            } else {
              Toast.warning(t('Cannot reject response'));
            }
          }
        );
      },
      () => {},
      t('Reject'),
      t('Close')
    );
  };

  acceptChangeRequestResponse = () => {
    const { t, match, changeRequestAcceptResponse } = this.props;
    Confirm(
      t('Accept response'),
      t("Are you sure, you want to accept HN's response? "),
      () => {
        changeRequestAcceptResponse(match.params.serviceRequestId).then(
          response => {
            if (response.success) {
              Toast.success(t('Change request successfully confirmed'));
              this.loadRequiredData();
            } else if (response.payload) {
              Toast.warning(response.payload.message);
            } else {
              Toast.warning(t('Cannot accept response'));
            }
          }
        );
      },
      () => {},
      t('Accept'),
      t('Close')
    );
  };

  rejectAdditionalRequestResponse = () => {
    const { t, match, createRejectResponse } = this.props;
    Confirm(
      t('Reject response'),
      t(
        "Are you sure, you want to reject HN's response? Service request will be marked as rejected"
      ),
      () => {
        createRejectResponse(match.params.serviceRequestId).then(response => {
          if (response.success) {
            Toast.success(t('Service request successfully removed'));
            this.loadRequiredData();
          } else if (response.payload) {
            Toast.warning(response.payload.message);
          } else {
            Toast.warning(t('Cannot reject response'));
          }
        });
      },
      () => {},
      t('Reject'),
      t('Close')
    );
  };

  acceptAdditionalRequestResponse = () => {
    const { t, match, createAcceptResponse } = this.props;
    Confirm(
      t('Accept response'),
      t("Are you sure, you want to accept HN's response? "),
      () => {
        createAcceptResponse(match.params.serviceRequestId).then(response => {
          if (response.success) {
            Toast.success(t('Service request successfully confirmed'));
            this.loadRequiredData();
          } else if (response.payload) {
            Toast.warning(response.payload.message);
          } else {
            Toast.warning(t('Cannot accept response'));
          }
        });
      },
      () => {},
      t('Accept'),
      t('Close')
    );
  };

  isOnlyPreviewMode() {
    const { hasUserPermission, serviceRequest } = this.props;
    return (
      hasUserPermission(USER_ROLES.READONLY) ||
      (hasUserPermission(USER_ROLES.HOST_UNIT) &&
        !serviceRequest.canHostUnitEdit)
    );
  }

  canUserAcceptResponse() {
    const { hasUserPermission } = this.props;
    return (
      hasUserPermission(USER_ROLES.ADMIN) ||
      hasUserPermission(USER_ROLES.SystemAdmin)
    );
  }

  canUserCreateTicket() {
    return !this.isOnlyPreviewMode();
  }

  formHasValueChanges(dirty) {
    return dirty || (this.state.itemChanged && !this.isActivityEmptyOnLoad());
  }

  getNoTicketsFooter() {
    const { t, footer } = this.props;
    return (
      <ServiceRequestFooter>
        {this.canUserCreateTicket() && (
          <>
            <RaisedButton
              type="button"
              large
              iconLeft={<Icon icon="plus" />}
              disabled={footer.isChangeDisabled}
              onClick={() => {
                this.setState({
                  showCreateChangeRequestDialog: true
                });
              }}
            >
              {t('Add change request')}
            </RaisedButton>
            <GhostButton
              type="button"
              disabled={footer.isCancellationDisabled}
              onClick={() => {
                this.setState({
                  showRequestCancellationDialog: true
                });
              }}
              danger
              large
              iconLeft={<Icon icon="Close" />}
            >
              {t('Request for cancellation')}
            </GhostButton>
          </>
        )}
      </ServiceRequestFooter>
    );
  }

  getAdditionalCreateAcceptFooter() {
    const { t } = this.props;
    return (
      <ServiceRequestFooter
        message={t(
          'Please accept or reject HN response to this service request'
        )}
      >
        {this.canUserAcceptResponse() && (
          <>
            <GhostButton
              type="button"
              danger
              large
              onClick={this.rejectAdditionalRequestResponse}
            >
              {t('Reject')}
            </GhostButton>
            <RaisedButton
              type="button"
              large
              onClick={this.acceptAdditionalRequestResponse}
            >
              {t('Accept')}
            </RaisedButton>
          </>
        )}
      </ServiceRequestFooter>
    );
  }

  getAdditionalEditableCreateAcceptFooter(dirty, isSubmitting) {
    const { t } = this.props;
    return (
      <ServiceRequestFooter
        message={t(
          'Please accept or reject HN response to this service request'
        )}
      >
        {!this.formHasValueChanges(dirty) && this.canUserAcceptResponse() && (
          <>
            <GhostButton
              type="button"
              danger
              large
              onClick={this.rejectAdditionalRequestResponse}
            >
              {t('Reject')}
            </GhostButton>
            <RaisedButton
              type="button"
              large
              onClick={this.acceptAdditionalRequestResponse}
            >
              {t('Accept')}
            </RaisedButton>
          </>
        )}
        {this.formHasValueChanges(dirty) && (
          <>
            <ServiceRequestNavContext.Consumer>
              {nav => (
                <GhostButton
                  onClick={() =>
                    this.cancelForm(dirty, nav.serviceRequestNavigation)
                  }
                  large
                >
                  {t('Cancel')}
                </GhostButton>
              )}
            </ServiceRequestNavContext.Consumer>
            <ServiceRequestNavContext.Consumer>
              {nav => (
                <RaisedButton
                  type="submit"
                  disabled={(!dirty && !this.state.itemChanged) || isSubmitting}
                  onClick={() => {
                    this.setState({
                      navigation: nav.serviceRequestNavigation,
                      navigationHistory: nav.previousPageHistory
                    });
                  }}
                  large
                >
                  {t('Save')}
                </RaisedButton>
              )}
            </ServiceRequestNavContext.Consumer>
          </>
        )}
      </ServiceRequestFooter>
    );
  }

  getChangeAcceptFooter() {
    const { t } = this.props;
    return (
      <ServiceRequestFooter
        message={t(
          'There is new response for this service request. Please accept HN response in order to confirm requested changes.'
        )}
      >
        {this.canUserAcceptResponse() && (
          <>
            <GhostButton
              type="button"
              danger
              large
              onClick={this.rejectChangeRequestResponse}
            >
              {t('Reject')}
            </GhostButton>
            <RaisedButton
              type="button"
              large
              onClick={this.acceptChangeRequestResponse}
            >
              {t('Accept')}
            </RaisedButton>
          </>
        )}
      </ServiceRequestFooter>
    );
  }

  getCancellationWaitingForHnFooter() {
    const { t } = this.props;
    return (
      <ServiceRequestFooter
        message={t(
          'Cancellation of this service request is not confirmed yet. Waiting for response from the HN.'
        )}
      />
    );
  }

  getAdditionalChangeWaitingForHnFooter() {
    const { t } = this.props;
    return (
      <ServiceRequestFooter
        message={t(
          'Changes for this service request are not confirmed yet. Waiting for response from the HN.'
        )}
      />
    );
  }

  createRequestCancellation = (
    values,
    { setSubmitting, setErrors },
    setDialogError,
    onDialogClose
  ) => {
    const { t, createCancellationRequest, match } = this.props;
    const data = {
      ...values,
      serviceRequestId: match.params.serviceRequestId
    };

    createCancellationRequest(data.serviceRequestId, data).then(response => {
      if (response.success) {
        Toast.success(t('Request for cancellation submitted successfully'));
        onDialogClose();
        this.loadRequiredData();
      } else if (response.payload && response.payload.details) {
        const errors = response.payload.details.reduce((results, error) => {
          return {
            ...results,
            [error.target]: error.message[0]
          };
        }, {});
        if (errors) {
          setErrors(errors);
        }
      } else if (response.payload) {
        setDialogError(response.payload.message);
      } else {
        setDialogError(t('Cannot request for cancellation'));
      }
      setSubmitting(false);
    });
  };

  renderHeaderCenterActions = () => {
    return null;
  };

  renderRequestCancellationDialog() {
    const { t } = this.props;
    const { showRequestCancellationDialog } = this.state;
    return (
      <ActionWithCommentDialog
        title={t('Request for cancellation')}
        commentLabel={t('Comment (why do you want to cancel service request)')}
        confirmText={t('Submit')}
        reload={this.getAssignmentServiceRequest}
        isDialogOpen={showRequestCancellationDialog}
        onSubmit={this.createRequestCancellation}
        closeDialog={() => {
          this.setState({
            showRequestCancellationDialog: false
          });
        }}
      />
    );
  }

  render() {
    const { isLoading, match, forSubmitData } = this.props;
    const { disabled, serviceRequest } = this.state;

    if (isLoading || (!serviceRequest.serviceRequestId && this.isEdit())) {
      return null;
    }

    if (
      (serviceRequest.activityId === null &&
        forSubmitData.activities &&
        forSubmitData.activities.length !== 0) ||
      this.isActivityNameDifferent()
    ) {
      this.setState({ itemChanged: true });
    }

    const editedFields = objectPath.get(serviceRequest, 'editedFields', []);
    return (
      <div className="service-request-custom--container" ref={this.container}>
        <ServiceRequestNavContext.Consumer>
          {nav =>
            nav.serviceRequestId !== match.params.serviceRequestId &&
            nav.setServiceRequestId(match.params.serviceRequestId)
          }
        </ServiceRequestNavContext.Consumer>
        <ServiceRequestNavContext.Consumer>
          {nav =>
            this.props.withEscNavigationCallback(
              this.navigateBack(nav.serviceRequestNavigation)
            )
          }
        </ServiceRequestNavContext.Consumer>
        {this.renderChangeRequestCreateDialog()}
        {this.renderRequestCancellationDialog()}
        <Formik
          validate={this.validation}
          validationSchema={this.validationSchema}
          validateOnBlur={false}
          validateOnChange={false}
          enableReinitialize={true}
          initialValues={this.formInitialValues()}
          onSubmit={this.onSubmit}
        >
          {({ setFieldValue, values, dirty, isSubmitting }) => (
            <ServiceRequestNavContext.Consumer>
              {nav => (
                <>
                  {this.renderHeader(dirty, nav, disabled, match)}
                  <Form>
                    {this.setState({
                      navigation: nav.serviceRequestNavigation,
                      navigationHistory: nav.previousPageHistory
                    })}
                    {this.renderPageActions(disabled, dirty, nav, isSubmitting)}
                    <div className="service-request-custom">
                      <div className="service-request-custom--panels">
                        <div>
                          {this.renderItemInformation(
                            disabled,
                            setFieldValue,
                            editedFields,
                            values
                          )}
                          {this.renderAdditionalDetails(
                            disabled,
                            editedFields,
                            setFieldValue
                          )}
                          <GeneralAttachments
                            disabled={true}
                            attachments={values.generalAttachments}
                            onFileSelect={this.handleFileUpload}
                          />
                        </div>
                        {this.renderDeliveryInformation(
                          setFieldValue,
                          values,
                          disabled,
                          editedFields
                        )}
                      </div>
                    </div>
                  </Form>
                </>
              )}
            </ServiceRequestNavContext.Consumer>
          )}
        </Formik>
        {this.renderAssignmentResponse()}
      </div>
    );
  }

  renderAssignmentResponse() {
    const { t } = this.props;
    const { serviceRequest } = this.state;

    if (!serviceRequest && !this.isEdit()) {
      return null;
    }

    return (
      <>
        {serviceRequest.response &&
          serviceRequest.response.canDeliveryQuantity >= 0 && (
            <div>
              <H2>{t('Response from HN')}</H2>
              <AssignmentResponse
                key={ShortId.generate()}
                assignmentResponse={serviceRequest.response}
                isDeliveryFileDialogOpen={this.state.deliveryFileDialogOpen}
                onDeliveryFileDialogOpen={this.onDeliveryFileDialogOpen}
                onDeliveryFileDialogClose={this.onDeliveryFileDialogClose}
                disabled={true}
                cancellation={serviceRequest.cancellation}
                isFileDialogOpen={this.state.responseFileDialogOpen}
                onFileDialogOpen={this.onResponseFileDialogOpen}
                onFileDialogClose={this.onResponseFileDialogClose}
              />
              {serviceRequest.cancellation && (
                <CancellationCosts cancellation={serviceRequest.cancellation} />
              )}
            </div>
          )}
        {serviceRequest &&
          serviceRequest.deliveryLines &&
          serviceRequest.deliveryLines.length > 0 && (
            <div style={{ marginTop: 10 }}>
              <H2>{t('Delivery from HN')}</H2>
              <AssignmentDelivery
                key={ShortId.generate()}
                deliveryLines={serviceRequest.deliveryLines}
                disabled={true}
                isFileDialogOpen={this.state.responseFileDialogOpen}
                onFileDialogOpen={this.onResponseFileDialogOpen}
                onFileDialogClose={this.onResponseFileDialogClose}
                isDeliveryFileDialogOpen={this.state.deliveryFileDialogOpen}
                onDeliveryFileDialogOpen={this.onDeliveryFileDialogOpen}
                onDeliveryFileDialogClose={this.onDeliveryFileDialogClose}
                showCurrency={true}
                exerciseCurrency={serviceRequest.currency}
                totals={serviceRequest.deliveryLineTotals}
              />
            </div>
          )}
        {serviceRequest.changeRequestResponse && (
          <div>
            <H2>
              <InputLabel
                indicationMessage={t('Response from HN to change request')}
              >
                {t('Response to change request')}
              </InputLabel>
            </H2>
            <AssignmentResponse
              key={ShortId.generate()}
              assignmentResponse={serviceRequest.changeRequestResponse}
              disabled={true}
              isFileDialogOpen={this.state.changeRequestResponseFileDialogOpen}
              onFileDialogOpen={this.onChangeRequestResponseFileDialogOpen}
              onFileDialogClose={this.onChangeRequestResponseFileDialogClose}
            />
          </div>
        )}
      </>
    );
  }

  isActivityNameDifferent() {
    return this.props.forSubmitData.activities &&
      this.props.forSubmitData.activities.find(
        x =>
          x.activityId === this.state.serviceRequest.activityId &&
          x.name !== this.state.serviceRequest.activityName
      )
      ? true
      : false;
  }

  isActivityEmptyOnLoad() {
    return this.state.serviceRequest.activityId === null;
  }

  renderDeliveryInformation(setFieldValue, values, disabled, editedFields) {
    const { forSubmitData, t, calculateByDaysData } = this.props;
    const { serviceRequest } = this.state;

    return (
      <Panel>
        <div className="service-request-custom--panel">
          <H2>{t('Delivery information')}</H2>
          <DeliveryInformation
            uoms={this.toUomsEntity(
              (serviceRequest.catalogItemId
                ? serviceRequest.unitsOfMeasure
                : forSubmitData.unitsOfMeasure) || []
            )}
            units={this.getUnits(forSubmitData.units)}
            setFieldValue={setFieldValue}
            checked={values.isCalculatedByDays}
            activities={this.toActivityEntity(
              this.props.forSubmitData.activities || []
            )}
            results={calculateByDaysData}
            showPlaceholderOption={!serviceRequest.catalogItemId}
            disabled={disabled}
            showTotalEstimates={values.rateFrom}
            editedFields={editedFields}
            values={values}
            activityInForm={!this.isEdit()}
          />

          {this.calculateCosts(values)}

          {serviceRequest.isTransportation === false && (
            <>
              <H5>{t('Location details')}</H5>
              <LocationDetails
                initialLocation={{
                  original: {
                    latitude: values.location
                      ? values.location.original.latitude
                      : '',
                    longtitude: values.location
                      ? values.location.original.longtitude
                      : '',
                    site: values.location ? values.location.original.site : '',
                    name: values.location ? values.location.original.name : '',
                    region: {
                      name: values.location
                        ? values.location.original.regionName
                        : ''
                    }
                  }
                }}
                defaultQuery={values.locationName ? values.locationName : ''}
                setFieldValue={setFieldValue}
                locationItems={this.toAutocompleteEntity(this.props.locations)}
                onQueryChange={this.onLocationQueryChange}
                disabled={disabled}
                editedFieldsHistory={{
                  location: FieldHistoryMessage('LocationTo', editedFields),
                  locationComment: FieldHistoryMessage(
                    'LocationCommentTo',
                    editedFields
                  ),
                  region: FieldHistoryMessage('RegionNameTo', editedFields),
                  longitude: FieldHistoryMessage('LongitudeTo', editedFields),
                  latitude: FieldHistoryMessage('LatitudeTo', editedFields),
                  site: FieldHistoryMessage('SiteTo', editedFields)
                }}
              />
            </>
          )}

          {serviceRequest.isTransportation === true &&
            this.renderTransportationLocations(
              t,
              serviceRequest,
              setFieldValue,
              disabled,
              editedFields
            )}
        </div>
      </Panel>
    );
  }

  renderTransportationLocations(
    t,
    serviceRequest,
    setFieldValue,
    disabled,
    editedFields
  ) {
    return (
      <>
        <H5>{t('Pickup location')}</H5>
        <LocationDetails
          initialLocation={{
            original: {
              latitude: serviceRequest.latitudeFrom,
              longtitude: serviceRequest.longitudeFrom,
              name: serviceRequest.locationName,
              site: serviceRequest.siteFrom,
              region: {
                name: serviceRequest.regionNameFrom
              }
            }
          }}
          defaultQuery={
            serviceRequest.locationNameFrom
              ? serviceRequest.locationNameFrom
              : ''
          }
          setFieldValue={setFieldValue}
          locationItems={this.toAutocompleteEntity(this.props.locations)}
          onQueryChange={this.onLocationQueryChange}
          isExpandable={true}
          schema={{
            location: 'locationFrom',
            locationComment: 'locationCommentFrom'
          }}
          disabled={disabled}
          editedFieldsHistory={{
            location: FieldHistoryMessage('LocationFrom', editedFields),
            locationComment: FieldHistoryMessage(
              'LocationCommentFrom',
              editedFields
            ),
            region: FieldHistoryMessage('RegionNameFrom', editedFields),
            longitude: FieldHistoryMessage('LongitudeFrom', editedFields),
            latitude: FieldHistoryMessage('LatitudeFrom', editedFields),
            site: FieldHistoryMessage('SiteFrom', editedFields)
          }}
        />
        <H5>{t('Delivery location')}</H5>
        <LocationDetails
          initialLocation={{
            original: {
              latitude: serviceRequest.latitude,
              longtitude: serviceRequest.longitude,
              site: serviceRequest.site,
              name: serviceRequest.locationName,
              region: {
                name: serviceRequest.regionName
              }
            }
          }}
          defaultQuery={
            serviceRequest.locationName ? serviceRequest.locationName : ''
          }
          setFieldValue={setFieldValue}
          locationItems={this.toAutocompleteEntity(this.props.locations)}
          onQueryChange={this.onLocationQueryChange}
          isExpandable={true}
          disabled={disabled}
          editedFieldsHistory={{
            location: FieldHistoryMessage('LocationTo', editedFields),
            locationComment: FieldHistoryMessage(
              'LocationCommentTo',
              editedFields
            ),
            region: FieldHistoryMessage('RegionNameTo', editedFields),
            longitude: FieldHistoryMessage('LongitudeTo', editedFields),
            latitude: FieldHistoryMessage('LatitudeTo', editedFields),
            site: FieldHistoryMessage('SiteTo', editedFields)
          }}
        />
      </>
    );
  }

  renderAdditionalDetails(disabled, editedFields, setFieldValue) {
    const { t } = this.props;

    return (
      <Panel>
        <div className="service-request-custom--panel">
          <H2>{t('Additional details')}</H2>
          <div>
            <Field
              name="comment"
              component={Textarea}
              placeholder={t('Describe additional details')}
              label={t('Comment')}
              disabled={disabled}
              indicationMessage={FieldHistoryMessage('Comment', editedFields)}
            />
          </div>
          <div>
            <InputLabel
              indicationMessage={FieldHistoryMessage(
                'AttachmentNames',
                editedFields
              )}
            >
              {t('Attachments')}:
            </InputLabel>
            <DropZone
              value={this.props.attachments}
              onSelect={files => {
                this.handleFileUpload(files);
              }}
              onChange={files => {
                setFieldValue('attachments', files);
              }}
              onRemove={file => {
                this.props.deleteAttachment(file);
              }}
              disabled={disabled}
            />
          </div>
        </div>
      </Panel>
    );
  }

  getAnnexWithInformation(disabled) {
    return this.props.forSubmitData.annexes &&
      this.props.forSubmitData.annexes.find(
        x => x.annexId == this.state.serviceRequest.annexId
      ) &&
      !disabled
      ? this.props.forSubmitData.annexes.find(
          x => x.annexId == this.state.serviceRequest.annexId
        )
      : null;
  }

  renderItemInformation(disabled, setFieldValue, editedFields, values) {
    const { t } = this.props;
    const { serviceRequest } = this.state;

    return (
      <Panel>
        <div className="service-request-custom--panel service-request-custom--panel--edit">
          <div className="service-request-custom--panel-top">
            <H2>{t('Item information')}</H2>
            {(!disabled || serviceRequest.canEditItem) && this.isEdit() && (
              <div className="service-request-custom--change-item">
                {this.state.showChangeItem && (
                  <Field
                    placeholder={t('Search items')}
                    name="changeItem"
                    items={this.toAutocompleteEntity(
                      this.props.oData.data,
                      'Code',
                      'Name',
                      true
                    )}
                    onItemSelect={selectedValues => {
                      this.changeItem(selectedValues, setFieldValue);
                    }}
                    onQueryChange={this.onItemQueryChange}
                    inputClass="service-request-custom--change-item-input"
                    component={Autocomplete}
                    noResultsMessage={t('No items found in the Catalog')}
                  />
                )}
                {serviceRequest.catalogItemCanBeChanged && (
                  <GhostButton onClick={this.toggleChangeItem}>
                    {this.state.showChangeItem ? t('Cancel') : t('Change item')}
                  </GhostButton>
                )}
              </div>
            )}
          </div>
          {serviceRequest.catalogItemId ? (
            <CatalogItemInfo
              title={serviceRequest.name}
              description={serviceRequest.description}
              rateFrom={serviceRequest.reateFrom}
              rateTo={serviceRequest.reateTo}
              currency={serviceRequest.currency}
              uom={serviceRequest.unitOfMeasureCode}
              attachments={serviceRequest.catalogItemAttachments}
              groupName={serviceRequest.groupName}
              annexName={serviceRequest.annexName}
              editedFields={editedFields}
              annexinformation={
                this.getAnnexWithInformation(false).annexInformation
              }
              catalogItemId={serviceRequest.catalogItemId}
            />
          ) : (
            this.renderCustomServiceRequestItemInfo(
              setFieldValue,
              t,
              disabled,
              editedFields,
              values
            )
          )}
        </div>
      </Panel>
    );
  }

  renderCustomServiceRequestItemInfo(
    setFieldValue,
    t,
    disabled,
    editedFields,
    values
  ) {
    return (
      <div>
        <div className="service-request-custom--row">
          <Field
            name="annexId"
            component={Dropdown}
            setFieldValue={setFieldValue}
            label={t('Annex')}
            placeholder={t('Select annex')}
            disabled={disabled}
            onChange={() => {
              setFieldValue('groupName', null);
              this.setState({ itemChanged: true });
            }}
            indicationMessage={FieldHistoryMessage('AnnexName', editedFields)}
          >
            {this.getAnnexOptions()}
          </Field>
          <Field
            name="groupName"
            component={Dropdown}
            setFieldValue={setFieldValue}
            label={t('Service group')}
            placeholder={t('Select group')}
            onChange={event => {
              setFieldValue('groupName', event.target.value);
              if (!values.annexId) {
                this.setAnnexValue(event, setFieldValue);
              }
            }}
            disabled={disabled}
            indicationMessage={FieldHistoryMessage('GroupName', editedFields)}
          >
            {this.getGroupOptions(values.annexId)}
          </Field>
          {[...this.props.forSubmitData.annexes].find(
            x => values.annexId == x.annexId && x.annexInformation
          ) && (
            <div className="informationTooltip">
              <InformationTooltip
                toolTipInformation={
                  [...this.props.forSubmitData.annexes].find(
                    x => values.annexId == x.annexId
                  ).annexInformation
                }
                startDisabled={this.isEdit() && !this.state.itemChanged}
              ></InformationTooltip>
            </div>
          )}
        </div>
        <div>
          <Field
            name="serviceName"
            component={Input}
            placeholder={t('Name')}
            disabled={disabled}
            indicationMessage={FieldHistoryMessage('ServiceName', editedFields)}
          >
            {t('Service name')}
          </Field>
        </div>
        <div>
          <Field
            name="serviceDescription"
            component={Textarea}
            placeholder={t('Description')}
            label={t('Service description')}
            disabled={disabled}
            indicationMessage={FieldHistoryMessage(
              'ServiceDescription',
              editedFields
            )}
          />
        </div>
      </div>
    );
  }

  renderHeader(dirty, nav, disabled, match) {
    const { t } = this.props;
    const { serviceRequest } = this.state;

    return (
      <div className="service-request-custom--heading">
        <div className="service-request-custom--header">
          <a
            href="/"
            onClick={event => {
              event.preventDefault();
              this.cancelForm(dirty, nav.serviceRequestNavigation);
            }}
          >
            <Icon
              icon="BackArrow"
              className="service-request-custom--header-back-icon"
            />
            <H1>
              {disabled
                ? t('Preview service request')
                : this.isEdit()
                ? this.getServiceRequestEditHeaderTitle()
                : this.getServiceRequestCreateHeaderTitle()}
              {this.isEdit() && <> - </>}
            </H1>
          </a>

          {this.isEdit() && (
            <>
              <div className="service-request-custom--header-unit">
                {serviceRequest.partyCode}, {serviceRequest.unitName} (
                {serviceRequest.requestNumber
                  ? serviceRequest.requestNumber
                  : '-'}
                )
              </div>
              <Status
                name={serviceRequest.statusGridName}
                type={serviceRequest.statusId}
              />
            </>
          )}
        </div>
        {this.renderHeaderCenterActions()}
        {this.isEdit() && (
          <PageLinks
            isDirty={dirty}
            serviceRequestId={match.params.serviceRequestId}
            partyId={match.params.partyId}
            discussion={{
              status: serviceRequest.discussionStatus,
              initials: this.getDiscussionInitials(),
              disabled: disabled,
              onDiscussionClose: this.loadRequiredData,
              isSeen: serviceRequest.isLastDiscussionSeen
            }}
            showPath={this.getShowPath()}
            editPath={this.getEditPath()}
            changeHistoryPath={this.getChangeHistoryPath()}
            historyPath={this.getHistoryPath()}
            discussionPath={this.getDiscussionPath()}
          />
        )}
      </div>
    );
  }

  getDefaultFooter(dirty, isSubmitting, nav) {
    const { t } = this.props;
    return (
      <>
        {this.isEdit() && (
          <GhostButton danger onClick={this.onRemove} large>
            {t('Remove this service request')}
          </GhostButton>
        )}
        <div className="service-request-custom--actions">
          <GhostButton
            onClick={() => this.cancelForm(dirty, nav.serviceRequestNavigation)}
            large
          >
            {t('Cancel')}
          </GhostButton>
          <RaisedButton
            type="submit"
            large
            disabled={(!dirty && !this.state.itemChanged) || isSubmitting}
          >
            {this.isAdditionalRequest() ? t('Submit') : t('Save')}
          </RaisedButton>
        </div>
      </>
    );
  }

  onConfirmServiceRequest(serviceRequestId) {
    this.setState({ isEarlyConfirmationSubmitting: true });
    const { history } = this.props;
    this.props.confirmServiceRequest(serviceRequestId).then(response => {
      if (response.success) {
        Toast.success('Service request confirmed');
        history.push(this.getBackNavigationUrl(this.state.navigation));
      } else if (response.payload) {
        Toast.warning(response.payload.message);
      } else {
        Toast.warning('Failed to confirm service request');
      }
      this.setState({ isEarlyConfirmationSubmitting: false });
    });
  }

  getEarlyConfirmFooter(serviceRequestId, dirty, isSubmitting) {
    const { t } = this.props;
    return (
      <ServiceRequestFooter
        message={t(
          'Early confirmation is required for this service request. Please confirm, change or remove this request.'
        )}
      >
        <GhostButton danger onClick={this.onRemove} large>
          {t('Remove this service request')}
        </GhostButton>
        <div>
          <ServiceRequestNavContext.Consumer>
            {nav => (
              <GhostButton
                onClick={() =>
                  this.cancelForm(dirty, nav.serviceRequestNavigation)
                }
                large
              >
                {t('Cancel')}
              </GhostButton>
            )}
          </ServiceRequestNavContext.Consumer>
          <ServiceRequestNavContext.Consumer>
            {nav =>
              !this.formHasValueChanges(dirty) ? (
                <RaisedButton
                  type="button"
                  onClick={() => this.onConfirmServiceRequest(serviceRequestId)}
                  disabled={this.state.isEarlyConfirmationSubmitting}
                  large
                >
                  {t('Confirm')}
                </RaisedButton>
              ) : (
                <RaisedButton
                  type="submit"
                  disabled={isSubmitting}
                  onClick={() => {
                    this.setState({
                      navigation: nav.serviceRequestNavigation,
                      navigationHistory: nav.previousPageHistory
                    });
                  }}
                  large
                >
                  {t('Save')}
                </RaisedButton>
              )
            }
          </ServiceRequestNavContext.Consumer>
        </div>
      </ServiceRequestFooter>
    );
  }

  getEarlyConfirmFooterForDisabled(serviceRequestId) {
    const { t } = this.props;
    return (
      <ServiceRequestFooter
        message={t(
          'Early confirmation is required for this service request. Please confirm this request.'
        )}
      >
        <RaisedButton
          type="button"
          onClick={() => this.onConfirmServiceRequest(serviceRequestId)}
          disabled={this.state.isEarlyConfirmationSubmitting}
          large
        >
          {t('Confirm')}
        </RaisedButton>
      </ServiceRequestFooter>
    );
  }

  getEarlyConfirmNoPermissionFooterForDisabled() {
    const { t } = this.props;
    return (
      <ServiceRequestFooter
        message={t(
          'Early confirmation to this service request required. Waiting for confirmation by SN responsible.'
        )}
      ></ServiceRequestFooter>
    );
  }

  renderPageActions(disabled, dirty, nav, isSubmitting) {
    const { footer } = this.props;
    const { serviceRequest } = this.state;

    const hasEarlyConfirmation =
      serviceRequest &&
      serviceRequest.response &&
      serviceRequest.response.earlyConfirmation;
    const earlyConfirmation = hasEarlyConfirmation
      ? serviceRequest.response.earlyConfirmation
      : null;

    const isEarlyConfirmationRequired = hasEarlyConfirmation
      ? earlyConfirmation.isEarlyConfirmationRequired
      : false;
    const earlyConfirmationIsEditable = hasEarlyConfirmation
      ? earlyConfirmation.isEditable
      : false;

    return (
      <PageActions container={this.container.current}>
        {!disabled &&
          (hasEarlyConfirmation &&
          isEarlyConfirmationRequired &&
          earlyConfirmationIsEditable ? (
            this.getEarlyConfirmFooter(
              serviceRequest.serviceRequestId,
              dirty,
              isSubmitting
            )
          ) : hasEarlyConfirmation &&
            isEarlyConfirmationRequired &&
            !earlyConfirmationIsEditable ? (
            this.getEarlyConfirmNoPermissionFooterForDisabled(
              dirty,
              isSubmitting,
              nav
            )
          ) : this.isAdditionalRequest &&
            footer &&
            (footer.footerType ===
              SERVICE_REQUEST_FOOTER_STATUSES.AdditionalCreateWaitingForHn ||
              footer.footerType ===
                SERVICE_REQUEST_FOOTER_STATUSES.AdditionalCreateConfirm ||
              footer.footerType ===
                SERVICE_REQUEST_FOOTER_STATUSES.AdditionalCreateAccept) ? (
            <>
              {(footer.footerType ===
                SERVICE_REQUEST_FOOTER_STATUSES.AdditionalCreateWaitingForHn ||
                footer.footerType ===
                  SERVICE_REQUEST_FOOTER_STATUSES.AdditionalCreateConfirm) &&
                this.getAdditionalEditableCreateWaitingForHnFooter(
                  dirty,
                  isSubmitting
                )}
              {footer.footerType ===
                SERVICE_REQUEST_FOOTER_STATUSES.AdditionalCreateAccept &&
                this.getAdditionalEditableCreateAcceptFooter(
                  dirty,
                  isSubmitting
                )}
            </>
          ) : (
            this.getDefaultFooter(dirty, isSubmitting, nav)
          ))}

        {disabled && footer && (
          <>
            {footer.footerType === SERVICE_REQUEST_FOOTER_STATUSES.None &&
              hasEarlyConfirmation &&
              isEarlyConfirmationRequired &&
              earlyConfirmationIsEditable &&
              this.getEarlyConfirmFooterForDisabled(
                serviceRequest.serviceRequestId
              )}
            {footer.footerType === SERVICE_REQUEST_FOOTER_STATUSES.None &&
              hasEarlyConfirmation &&
              isEarlyConfirmationRequired &&
              !earlyConfirmationIsEditable &&
              this.getEarlyConfirmNoPermissionFooterForDisabled()}
            {(footer.footerType ===
              SERVICE_REQUEST_FOOTER_STATUSES.AdditionalCreateWaitingForHn ||
              footer.footerType ===
                SERVICE_REQUEST_FOOTER_STATUSES.AdditionalCreateConfirm) &&
              this.getAdditionalCreateWaitingForHnFooter(
                dirty,
                isSubmitting,
                serviceRequest.canEditItem
              )}
            {footer.footerType ===
              SERVICE_REQUEST_FOOTER_STATUSES.AdditionalCreateAccept &&
              this.getAdditionalCreateAcceptFooter()}
            {footer.footerType ===
              SERVICE_REQUEST_FOOTER_STATUSES.RequestForCancellation &&
              this.getNoTicketsFooter()}
            {(footer.footerType ===
              SERVICE_REQUEST_FOOTER_STATUSES.AdditionalChangeWaitingForHn ||
              footer.footerType ===
                SERVICE_REQUEST_FOOTER_STATUSES.AdditionalChangeConfirm) &&
              this.getAdditionalChangeWaitingForHnFooter()}
            {footer.footerType ===
              SERVICE_REQUEST_FOOTER_STATUSES.AdditionalChangeAccept &&
              this.getChangeAcceptFooter()}
            {(footer.footerType ===
              SERVICE_REQUEST_FOOTER_STATUSES.AdditionalCancellationWaitingForHn ||
              footer.footerType ===
                SERVICE_REQUEST_FOOTER_STATUSES.AdditionalCancellationConfirm) &&
              this.getCancellationWaitingForHnFooter()}
            {footer.footerType ===
              SERVICE_REQUEST_FOOTER_STATUSES.AdditionalChangeWaitingForSn &&
              this.getAdditionalChangeWaitingForSnFooter()}
            {footer.footerType ===
              SERVICE_REQUEST_FOOTER_STATUSES.AdditionalCreateWaitingForSn &&
              this.getAdditionalCreateWaitingForSnFooter()}
          </>
        )}
      </PageActions>
    );
  }

  renderChangeRequestCreateDialog() {
    const { forSubmitData } = this.props;
    const { serviceRequest } = this.state;

    return (
      <ChangeRequestCreateDialog
        isDialogOpen={this.state.showCreateChangeRequestDialog}
        reload={this.loadRequiredData}
        closeDialog={() => {
          this.setState({
            showCreateChangeRequestDialog: false
          });
        }}
        unitsOptions={
          forSubmitData && forSubmitData.units
            ? this.getUnits(forSubmitData.units).map(unit => {
                return (
                  <option value={unit.value} key={unit.value}>
                    {unit.title}
                  </option>
                );
              })
            : []
        }
        uomsOptions={
          forSubmitData && forSubmitData.unitsOfMeasure
            ? this.toUomsEntity(forSubmitData.unitsOfMeasure).map(uom => {
                return (
                  <option value={uom.value} key={uom.key}>
                    {uom.title}
                  </option>
                );
              })
            : []
        }
        activities={forSubmitData.activities}
        serviceRequest={serviceRequest}
        serviceRequestId={serviceRequest.serviceRequestId}
      />
    );
  }
}

ServiceRequestCustom.propTypes = {
  oData: PropTypes.object,
  forSubmit: PropTypes.func,
  isLoading: PropTypes.bool,
  forSubmitData: PropTypes.object,
  queryLocations: PropTypes.func,
  t: PropTypes.func,
  match: PropTypes.object,
  calculateByDays: PropTypes.func,
  clearCalculateByDays: PropTypes.func,
  calculateByDaysData: PropTypes.object,
  queryOData: PropTypes.func,
  resetOData: PropTypes.func,
  create: PropTypes.func,
  update: PropTypes.func,
  updateCustom: PropTypes.func,
  confirmServiceRequest: PropTypes.func,
  locations: PropTypes.array,
  history: PropTypes.object,
  getServiceRequest: PropTypes.func,
  remove: PropTypes.func,
  serviceRequest: PropTypes.object,
  uploadAttachments: PropTypes.func,
  setAttachments: PropTypes.func,
  deleteAttachment: PropTypes.func,
  clearAttachments: PropTypes.func,
  attachments: PropTypes.array,
  location: PropTypes.object,
  hasUserPermission: PropTypes.func,
  createCustomAdditionalServiceRequest: PropTypes.func,
  footer: PropTypes.object,
  getFooter: PropTypes.func,
  createRejectResponse: PropTypes.func,
  createAcceptResponse: PropTypes.func,
  changeRequestAcceptResponse: PropTypes.func,
  changeRequestRejectResponse: PropTypes.func,
  createCancellationRequest: PropTypes.func,
  clearFooter: PropTypes.func,
  withEscNavigationCallback: PropTypes.func
};

export default ServiceRequestCustom;
